import React, { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import {
  Box,
  IconButton,
  TableCell,
  Badge,
  Tooltip,
  Chip,
  Checkbox,
  alpha,
} from "@mui/material";
import { formatRegionMessage } from "bundles/common/utils/utils";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import PersonIcon from "@mui/icons-material/Person";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CloseIcon from "@mui/icons-material/Close";
import CopyButton from "bundles/Internal/components/common/CopyButton";
import CandidateTableFilters from "bundles/Internal/components/candidate/CandidateTableFilters";
import DataTable from "bundles/Internal/components/common/DataTable";
import { CircleUnchecked, CircleChecked } from "bundles/Internal/components/common/CheckIcons";
import UserAvatar from "bundles/Internal/components/common/UserAvatar";
import FollowButton from "bundles/Internal/components/common/FollowButton";
import JobApplicationsCollapse from "bundles/Internal/components/common/JobApplicationsCollapse";

export const COLUMNS_CONSTANTS = [
  { key: "id", label: "Id" },
  { key: "godview_url", label: "Godview" },
  { key: "first_name", label: "First name" },
  { key: "last_name", label: "Last name" },
  { key: "region", label: "Region" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "resume_url", label: "Resume" },
  { key: "city", label: "City" },
  { key: "zipcode", label: "Zipcode" },
  { key: "job_applications_count", label: "Job applications count" },
  { key: "profile_completion", label: "Profile completion" },
  { key: "created_at", label: "Created at" },
  { key: "created_by", label: "Created by" },
  { key: "updated_at", label: "Updated at" },
  { key: "signup_origin", label: "Signup origin" },
  { key: "availability", label: "Availability" },
  { key: "work_permit", label: "Work permit" },
  { key: "desired_sector", label: "Desired sector" },
  { key: "experience_year", label: "Experience year" },
  { key: "candidate_status", label: "Candidate status" },
  { key: "country", label: "Country" },
  { key: "whitelisted", label: "Whitelisted" },
  { key: "blacklist_reasons", label: "Blacklist reasons" },
  { key: "current_stage_name", label: "Current stage" },
  { key: "job_title", label: "Job title" },
  { key: "desired_job_title", label: "Desired job title" },
  { key: "desired_job_type", label: "Desired job type" },
  { key: "desired_locations", label: "Desired locations" },
  { key: "highest_degree", label: "Highest degree" },
  { key: "utm_source", label: "Utm source" },
  { key: "utm_campaign", label: "Utm campaign" },
  { key: "utm_medium", label: "Utm medium" },
  { key: "current_sign_in_at", label: "Current sign in at" },
  { key: "deleted_at", label: "Deleted at" },
  { key: "last_sign_in_at", label: "Last sign in at" },
  { key: "portfolio_url", label: "Portfolio url" },
  { key: "preferred_locale", label: "Preferred locale" },
  { key: "presentation", label: "Presentation" },
  { key: "sign_in_count", label: "Sign in count" },
  { key: "candidate_interests_count", label: "Candidate interests count" },
  { key: "blacklisted_company_ids", label: "Blacklisted company ids" },
  { key: "password_generated", label: "Password generated" },
];

export const AVAILABILITY_COLOR = {
  available: { border: "#079344", bg: "rgba(7,147,68,0.10)", text: "#079344" },
  unavailable: { border: "#D32F2F", bg: "rgba(211,47,47,0.10)", text: "#D32F2F" },
  looking: { border: "#1976D2", bg: "rgba(25,118,210,0.10)", text: "#1976D2" },
  listening: { border: "#7B1FA2", bg: "rgba(123,31,162,0.10)", text: "#7B1FA2" },
  currently_employed: { border: "#F57C00", bg: "rgba(245,124,0,0.10)", text: "#F57C00" },
};

export const SIGNUP_ORIGIN_COLOR = {
  form: { border: "#079344", bg: "rgba(7,147,68,0.10)", text: "#079344" },
  linkedin: { border: "#0A66C2", bg: "rgba(10,102,194,0.10)", text: "#0A66C2" },
  facebook: { border: "#1877F2", bg: "rgba(24,119,242,0.10)", text: "#1877F2" },
  google: { border: "#DB4437", bg: "rgba(219,68,55,0.10)", text: "#DB4437" },
  microsoft: { border: "#737373", bg: "rgba(115,115,115,0.10)", text: "#737373" },
  landbot: { border: "#6A1B9A", bg: "rgba(106,27,154,0.10)", text: "#6A1B9A" },
};

export const CANDIDATE_STATUS_COLOR = {
  independent_type: { border: "#6A1B9A", bg: "rgba(106,27,154,0.10)", text: "#6A1B9A" },
  freelance: { border: "#1976D2", bg: "rgba(25,118,210,0.10)", text: "#1976D2" },
  fixedterm: { border: "#F57C00", bg: "rgba(245,124,0,0.10)", text: "#F57C00" },
  permanent: { border: "#079344", bg: "rgba(7,147,68,0.10)", text: "#079344" },
  trainee: { border: "#7B1FA2", bg: "rgba(123,31,162,0.10)", text: "#7B1FA2" },
  bachelor: { border: "#455A64", bg: "rgba(69,90,100,0.10)", text: "#455A64" },
  master: { border: "#0A66C2", bg: "rgba(10,102,194,0.10)", text: "#0A66C2" },
  training: { border: "#DB4437", bg: "rgba(219,68,55,0.10)", text: "#DB4437" },
  independent: { border: "#6D4C41", bg: "rgba(109,76,65,0.10)", text: "#6D4C41" },
  employee: { border: "#2E7D32", bg: "rgba(46,125,50,0.10)", text: "#2E7D32" },
  workman: { border: "#5D4037", bg: "rgba(93,64,55,0.10)", text: "#5D4037" },
  unemployed: { border: "#D32F2F", bg: "rgba(211,47,47,0.10)", text: "#D32F2F" },
  student: { border: "#1565C0", bg: "rgba(21,101,192,0.10)", text: "#1565C0" },
};

export const PREFERRED_LOCALE_COLOR = {
  en: { border: "#1976D2", bg: "rgba(25,118,210,0.10)", text: "#1976D2" },
  fr: { border: "#079344", bg: "rgba(7,147,68,0.10)", text: "#079344" },
  nl: { border: "#F57C00", bg: "rgba(245,124,0,0.10)", text: "#F57C00" },
};

export const HIGHEST_DEGREE_COLOR = {
  no_degree: { border: "#616161", bg: "rgba(97,97,97,0.10)", text: "#616161" },
  highschool: { border: "#455A64", bg: "rgba(69,90,100,0.10)", text: "#455A64" },
  bachelor: { border: "#1976D2", bg: "rgba(25,118,210,0.10)", text: "#1976D2" },
  master: { border: "#0A66C2", bg: "rgba(10,102,194,0.10)", text: "#0A66C2" },
  phd: { border: "#6A1B9A", bg: "rgba(106,27,154,0.10)", text: "#6A1B9A" },
};

const pill = (label, c) => (
  <Chip
    label={String(label).replaceAll("_", " ").toUpperCase()}
    size="small"
    sx={{
      backgroundColor: c.bg,
      color: c.text,
      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      fontSize: 11,
      fontWeight: 600,
      borderRadius: "8px",
      height: 24,
    }}
  />
);

const formatValue = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("fr-BE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
};

const DATE_KEYS = new Set([
  "created_at",
  "updated_at",
  "current_sign_in_at",
  "last_sign_in_at",
]);

function formatCandidateCell(candidate, key) {
  const value = candidate?.[key];

  if (key === "region" && value) {
    return formatRegionMessage(value);
  }

  if (key === "desired_locations" && Array.isArray(value)) {
    return value.map((l) => (typeof l === "string" ? l : l?.label)).filter(Boolean).join(", ");
  }

  if (key === "work_permit" || key === "whitelisted" || key === "password_generated") {
    if (value) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Checkbox
            checked
            disabled
            size="small"
            sx={{ color: "#079344 !important", p: 1 }}
          />
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 1 }}>
        <CloseIcon fontSize="small" sx={{ color: "#d32f2f", fontSize: "20px" }} />
      </Box>
    );
  }

  if (DATE_KEYS.has(key)) {
    return formatDateTime(value);
  }

  if (key === "created_by") {
    const creator = candidate?.created_by;
    if (!creator) {
      return (
        <Box component="span" sx={{ fontSize: 12, color: "rgba(42,51,78,0.45)", fontStyle: "italic" }}>
          Unknown
        </Box>
      );
    }
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <UserAvatar user={creator} size={22} />
        <Box component="span">{creator.full_name || creator.email || "Unknown"}</Box>
      </Box>
    );
  }

  if (key === "availability" && value) return pill(value, AVAILABILITY_COLOR[value] || AVAILABILITY_COLOR.available);
  if (key === "signup_origin" && value) return pill(value, SIGNUP_ORIGIN_COLOR[value] || SIGNUP_ORIGIN_COLOR.form); 
  if (key === "candidate_status" && value) return pill(value, CANDIDATE_STATUS_COLOR[value] || SIGNUP_ORIGIN_COLOR.form);
  if (key === "preferred_locale" && value) return pill(value, PREFERRED_LOCALE_COLOR[value] || SIGNUP_ORIGIN_COLOR.form);
  if (key === "highest_degree" && value) return pill(value, HIGHEST_DEGREE_COLOR[value] || SIGNUP_ORIGIN_COLOR.form);

  const URL_FIELDS = {
    godview_url: "Godview",
    resume_url: "Resume",
    portfolio_url: "Portfolio",
  };

  if (key === "email" || key === "phone") {
    if (!value) return "";
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0, maxWidth: 200 }}>
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: 1 }}>{value}</Box>
        <CopyButton value={value} sx={{ flexShrink: 0 }} />
      </Box>
    );
  }

  if (URL_FIELDS[key]) {
    if (!value) return "";
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#079344", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", textDecoration: "none" }}
        onMouseOver={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
        onMouseOut={(e) => { e.currentTarget.style.textDecoration = "none"; }}
        onClick={(e) => e.stopPropagation()}
      >
        {URL_FIELDS[key]} ↗
      </a>
    );
  }

  return formatValue(value);
}

const CandidateTable = ({
  candidates = [],
  onCandidateSelect,
  onOpenCreateCandidate,
  onApplyToJob,
  onOpenNotes,
  onQueryChange,
  filters,
  onFiltersChange,
  creators = [],
  page,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  loading = false,
  selectedCandidateIds = [],
  onSelectCandidate,
  onSelectAll,
  followedCandidateIds,
  onToggleFollow,
}) => {
  const intl = useIntl();
  const [expandedRows, setExpandedRows] = useState({});

  const toggleExpand = useCallback((e, candidateId) => {
    e.stopPropagation();
    setExpandedRows((prev) => ({ ...prev, [candidateId]: !prev[candidateId] }));
  }, []);

  const renderExpandedRow = useCallback(
    (candidate) => {
      if (!expandedRows[candidate.id]) return null;
      return (
        <JobApplicationsCollapse
          applications={candidate.job_applications || []}
          variant="candidate"
          emptyLabel="No job applications for this candidate."
        />
      );
    },
    [expandedRows],
  );

  const allSelected = candidates.length > 0 && candidates.every((c) => selectedCandidateIds.includes(c.id));
  const someSelected = selectedCandidateIds.length > 0 && !allSelected;

  const columns = useMemo(
    () =>
      COLUMNS_CONSTANTS.map((col) =>
        col.key === "region"
          ? {
            ...col,
            label: intl.formatMessage({ id: "candidate.region", defaultMessage: "Region" }),
          }
          : col,
      ),
    [intl],
  );

  const renderCell = useCallback((candidate, key) => formatCandidateCell(candidate, key), []);

  return (
    <DataTable
      title="Candidates"
      totalLabel={(count) => `${count.toLocaleString()} total candidates`}
      emptyIcon={PersonIcon}
      emptyTitle="No candidates found"
      emptySubtitle="Try adjusting your search criteria"
      columns={columns}
      data={candidates}
      renderCell={renderCell}
      expandable
      isRowExpanded={(candidate) => !!expandedRows[candidate.id]}
      onToggleExpandRow={(candidate, e) => toggleExpand(e, candidate.id)}
      extraHeadColumns={[
        <Checkbox
          key="select-all"
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => onSelectAll?.(e.target.checked)}
          disableRipple
          icon={<CircleUnchecked />}
          checkedIcon={<CircleChecked />}
          indeterminateIcon={
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                backgroundColor: "hsl(150 38% 17%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxSizing: "border-box",
              }}
            >
              <Box sx={{ width: 8, height: 2, backgroundColor: "hsl(80 97% 72%)", borderRadius: 1 }} />
            </Box>
          }
          sx={{ p: 0, "&:hover .MuiBox-root": { borderColor: "hsl(150 38% 17%)" } }}
        />,
        "Notes"
      ]}
      renderExtraCells={(candidate) => {
        const notesCount = Number(candidate?.notes_count || 0);
        const isSelected = selectedCandidateIds.includes(candidate.id);
        return (
          <>
            <TableCell
              key="select"
              sx={{ whiteSpace: "nowrap", py: 1.5, width: 48 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Checkbox
                checked={isSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  onSelectCandidate?.(candidate.id, e.target.checked);
                }}
                disableRipple
                icon={<CircleUnchecked />}
                checkedIcon={<CircleChecked />}
                sx={{ p: 0, "&:hover .MuiBox-root": { borderColor: "hsl(150 38% 17%)" } }}
              />
            </TableCell>
            <TableCell key="notes" sx={{ whiteSpace: "nowrap", py: 1.5 }}>
              <Tooltip title="View notes" arrow placement="top">
                <IconButton
                  size="small"
                  aria-label="Open notes"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenNotes?.(candidate);
                  }}
                  disabled={!onOpenNotes}
                  sx={{
                    backgroundColor:
                              notesCount > 0
                                ? alpha("#079344", 0.1)
                                : "transparent",
                    "&:hover": {
                      backgroundColor: alpha("#079344", 0.2),
                    },
                  }}
                >
                  <Badge
                    badgeContent={notesCount}
                    color="primary"
                    invisible={notesCount === 0}
                    overlap="circular"
                    sx={{
                      "& .MuiBadge-badge": {
                        backgroundColor: "#079344",
                        fontWeight: 600,
                        fontSize: 10,
                      },
                    }}
                  >
                    <NoteAddOutlinedIcon
                      fontSize="small"
                      sx={{
                        color:
                                  notesCount > 0 ? "#079344" : "text.secondary",
                      }}
                    />
                  </Badge>
                </IconButton>
              </Tooltip>
            </TableCell>
          </>
        );
      }}
      renderExpandedRow={renderExpandedRow}
      tailHeadColumns={["Follow"]}
      renderTailCells={(candidate) => (
        <TableCell key="follow" sx={{ whiteSpace: "nowrap", py: 1.5, width: 48 }} onClick={(e) => e.stopPropagation()}>
          <FollowButton
            followableType="Candidate"
            followableId={candidate.id}
            followed={followedCandidateIds ? followedCandidateIds.has(candidate.id) : Boolean(candidate.followed)}
            onChange={(next) => onToggleFollow?.(candidate.id, next)}
          />
        </TableCell>
      )}
      onRowClick={onCandidateSelect}
      onAddClick={onOpenCreateCandidate}
      addButtonLabel="Create candidate"
      extraActions={onApplyToJob ? [{
        label: "Apply to job",
        onClick: onApplyToJob,
        icon: <WorkOutlineIcon sx={{ fontSize: 16 }} />,
      }] : []}
      onQueryChange={onQueryChange}
      searchPlaceholder="Search by name, email..."
      searchInfo="Full name, first name, last name, email, phone"
      searchById
      filtersComponent={
        filters && onFiltersChange ? (
          <CandidateTableFilters filters={filters} onFiltersChange={onFiltersChange} creators={creators} />
        ) : null
      }
      page={page}
      totalCount={totalCount}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      loading={loading}
    />
  );
};

export default CandidateTable;
